import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteReleaseSoundRecordingMutation, getTrackQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteReleaseSoundRecording = (trackId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteReleaseSoundRecordingAction, { loading }] = useMutation(deleteReleaseSoundRecordingMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getTrackQuery,
                variables: {
                    where: { id: trackId },
                },
            },
        ],
    });

    const { deleteReleaseSoundRecordingPending: deleteReleaseSoundRecordingPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteReleaseSoundRecording = async (id) => {
        try {
            await deleteReleaseSoundRecordingAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", {
                                entity: `${t("releases.entity")} ${t("labels.soundRecording")}`,
                            }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteReleaseSoundRecordingPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(
                    () =>
                        t("messages.deleteMsgs.pending", {
                            entity: `${t("releases.entity")} ${t("labels.soundRecording")}`,
                        }),
                    { toastId: `${deleteReleaseSoundRecordingPendingToastName}_${toastId}` },
                );
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteReleaseSoundRecording };
};

export default useDeleteReleaseSoundRecording;
